"use client";

import { useRetailer } from "@faire/retailer-visitor-shared/hooks/retailer/useRetailer";
import { useUserStore } from "@faire/retailer-visitor-shared/stores/domain/useUserStore";
import { useStrictLocalization } from "@faire/web--source/common/localization/localize";
import { IRetailer } from "@faire/web-api--source/indigofair/data/IRetailer";
import { IRetailerLaunchWithFaireApplication } from "@faire/web-api--source/indigofair/data/IRetailerLaunchWithFaireApplication";
import isEmpty from "lodash/isEmpty";
import { useEffect } from "react";
import { create } from "zustand";

import { useOwFIDVTierLogic } from "../useOwFIDVTierLogic";
import { getDefaultStoreOpeningDate } from "../utils";

interface OwFStore {
  formData: IRetailerLaunchWithFaireApplication;
  updateFormField: <K extends keyof IRetailerLaunchWithFaireApplication>(
    field: K,
    value: IRetailerLaunchWithFaireApplication[K]
  ) => void;
  updateFormData: (
    newFormData: Partial<IRetailerLaunchWithFaireApplication>
  ) => void;

  isStoreAddressFormVisible: boolean;
  setIsStoreAddressFormVisible: (isStoreAddressFormVisible: boolean) => void;
}

export const useOwFStore = create<OwFStore>((set) => ({
  formData: IRetailerLaunchWithFaireApplication.build({}),

  updateFormField: (field, value) =>
    set((state) => ({
      formData: {
        ...state.formData,
        [field]: value,
      },
    })),
  updateFormData: (newFormData) =>
    set((state) => ({
      formData: {
        ...state.formData,
        ...newFormData,
      },
    })),

  isStoreAddressFormVisible: false,
  setIsStoreAddressFormVisible: (isStoreAddressFormVisible) =>
    set({ isStoreAddressFormVisible }),
}));

export const useOwFInitializer = (
  initialValues?: Partial<IRetailerLaunchWithFaireApplication>,
  isSignUp?: boolean
) => {
  const { strictLocalize } = useStrictLocalization();

  const { defaultAddress, fullName } = useUserStore([
    "defaultAddress",
    "fullName",
  ]);
  const { retailerName, retailer } = useRetailer();
  const { isAnyTierActive } = useOwFIDVTierLogic();

  const updateFormData = useOwFStore((state) => state.updateFormData);
  const setIsStoreAddressFormVisible = useOwFStore(
    (state) => state.setIsStoreAddressFormVisible
  );

  useEffect(() => {
    const defaultStoreAddress = (() => {
      if (!isEmpty(defaultAddress)) {
        return defaultAddress;
      }
      if (!isEmpty(retailer?.address)) {
        return retailer?.address;
      }
      return undefined;
    })();

    const preFilledOpeningRange = getDefaultStoreOpeningDate(
      strictLocalize,
      retailer?.launch_with_faire?.store_opening_month,
      retailer?.reported_creation_time
    );

    const prefilledWebsiteUrl =
      isSignUp || !isAnyTierActive ? retailer?.website_url : undefined;

    const prefilledWebsiteStatus =
      retailer?.business_category === IRetailer.BusinessCategory.ONLINE_ONLY &&
      !isAnyTierActive
        ? IRetailerLaunchWithFaireApplication.HasStoreWebsite.HAS_WEBSITE
        : undefined;

    const initialFormData = IRetailerLaunchWithFaireApplication.build({
      ...initialValues,
      full_name: fullName,
      store_name: retailerName,
      store_address: defaultStoreAddress,
      website_url: prefilledWebsiteUrl,
      opening_range: preFilledOpeningRange,
      credit_check_authorized: false,
      online_has_store_website: prefilledWebsiteStatus,
    });

    setIsStoreAddressFormVisible(!isEmpty(initialFormData?.store_address));

    // Update each field individually
    updateFormData(initialFormData);
  }, [
    defaultAddress,
    fullName,
    retailerName,
    retailer,
    isAnyTierActive,
    strictLocalize,
    isSignUp,
    updateFormData,
    initialValues,
    setIsStoreAddressFormVisible,
  ]);
};
